import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Col } from '../../col';
import { Progress, type ProgressProperties } from '../../progress';
import { Row } from '../../row';
import { Tag, type TagProperties } from '../../tag';
import { Text, Title } from '../../typography';
import { DataCardDescription, type DataCardDescriptionProperties } from '../data-card-description';
import { DataCardParticipants, type ParticipantsProperties } from '../data-card-participants';
import { DataCardPrice, type PricesProperties } from '../data-card-price';

export type CardProgressProperties = {
  color?: ProgressProperties['color'];
  max: number;
  size?: ProgressProperties['size'];
  value: number;
};

type P = {
  className?: string;
  description?: DataCardDescriptionProperties;
  participantsProperties?: ParticipantsProperties;
  pricesProperties?: PricesProperties;
  progressProperties?: CardProgressProperties;
  tag?: TagProperties | TagProperties[];
  title: string;
  titleEllipsis?: { rows: number; tooltip: boolean };
};

const DataCardBody = ({
  className,
  description,
  participantsProperties,
  pricesProperties,
  progressProperties,
  tag,
  title,
  titleEllipsis,
}: P) => {
  const { t } = useTranslation();
  const percent = useMemo(
    () => (progressProperties ? (progressProperties.value / progressProperties.max) * 100 : 0),
    [progressProperties],
  );
  // eslint-disable-next-line sonarjs/no-nested-conditional -- TODO: fix later
  const tagItems = tag ? (Array.isArray(tag) ? tag : [tag]) : null;

  return (
    <Row className={className}>
      <Col span={24}>
        {tagItems && tagItems.length > 0 ? (
          <Row gutter={['xs', 0]}>
            {tagItems.map((tagItem, index) => (
              // eslint-disable-next-line react/no-array-index-key -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
              <Col key={index}>
                <Tag {...tagItem}>{tagItem.children}</Tag>
              </Col>
            ))}
          </Row>
        ) : null}
      </Col>
      <Col span={24}>
        {titleEllipsis ? (
          <Title ellipsis={titleEllipsis} level={5} marginBottom={false} marginTop={Boolean(tagItems?.length)}>
            {title}
          </Title>
        ) : (
          <Text type="title" weight="bold" withinLink={{ hasHoverEffect: true }}>
            {t(title)}
          </Text>
        )}
      </Col>
      {description ? (
        <Col span={24}>
          <DataCardDescription description={description} />
        </Col>
      ) : null}
      {participantsProperties ? (
        <Col span={24}>
          <DataCardParticipants participants={participantsProperties} />
        </Col>
      ) : null}
      {progressProperties ? (
        <Col span={24}>
          <Progress
            color={progressProperties.color}
            percent={percent}
            showInfo={false}
            size={progressProperties.size}
          />
        </Col>
      ) : null}
      {pricesProperties ? (
        <Col span={24}>
          <DataCardPrice prices={pricesProperties} />
        </Col>
      ) : null}
    </Row>
  );
};

export default DataCardBody;
